import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Loader from "../Loader/Loader";
import { toast } from "react-toastify";
import { getHeaders, uploadFileToIPFS } from "../../utils/helper";
import { BASE_URL } from "../../utils/constant";
import axios from "axios";
import { usePublicClient, useWalletClient } from "wagmi";
import contractData from "../../Contract/ERC721Token.json";
import { useAppKitAccount } from "@reown/appkit/react";
import useApi from "../../hook/useApi";
import { isAddress } from "viem";

const defaultCollection = {
  name: "",
  symbol: "",
  contractURI: "",
  saleRecipient: "",
  royaltyRecipient: "",
  royaltyBps: "",
  trustedForwarder: "",
  category: "",
  image: "",
  description: "",
};

const AddNftCollection = ({
  show,
  handleClose,
  mode = "create",
  data = {},
}) => {
  const isView = mode === "view";
  const isUpdate = mode === "update";
  const isCreate = mode === "create";

  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { isConnected } = useAppKitAccount();

  const { AsyncGetApiCall } = useApi();
  const [formData, setFormData] = useState(defaultCollection);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  console.log("isLoading : ", loading);
  console.log("ERRORS : ", errors);

  // ✅ Fetch categories when modal opens
  useEffect(() => {
    if (show) fetchCategories();
  }, [show]);

  const fetchCategories = async () => {
    try {
      const res = await AsyncGetApiCall(`/category/active-list`);
      if (res?.success) setCategories(res.data || []);
      else toast.error(res?.message || "Failed to fetch categories");
    } catch (error) {
      toast.error("Error fetching categories");
      console.error(error);
    }
  };

  // ✅ Populate form data when editing or viewing
  useEffect(() => {
    if (show) {
      if (isUpdate || isView) {
        setFormData({
          ...defaultCollection,
          ...data,
          image: data?.image || "",
          symbol: data?.symbol || "",
        });
        if (data?.image) setImagePreview(`${BASE_URL}/uploads/${data.image}`);
      } else {
        setFormData(defaultCollection);
        setImagePreview(null);
      }
      setErrors({});
    }
  }, [show, mode, data]);

  // ✅ Validation function
  const validate = () => {
    const newErrors = {};
    const validChars = /^[a-zA-Z0-9\s_-]+$/;

    const requiredFields = [
      "name",
      "symbol",
      "saleRecipient",
      "royaltyRecipient",
      "royaltyBps",
      "category",
      "image",
      "description",
    ];

    requiredFields.forEach((field) => {
      const value = formData[field]?.toString().trim();

      const fieldName = field.charAt(0).toUpperCase() + field.slice(1);

      // if (!value) newErrors[field] = `${field} is required`;
      if (!value) newErrors[field] = `${fieldName} is required`;
    });

    // ✅ Address validation
    ["saleRecipient", "royaltyRecipient"].forEach((field) => {
      const value = formData[field]?.trim();
      if (value && !isAddress(value)) {
        newErrors[field] = `${field} must be a valid EVM address`;
      }
    });

    if (
      formData.name &&
      (formData.name.length < 2 || formData.name.length > 50)
    )
      newErrors.name = "Name must be 2–50 characters";
    else if (formData.name && !validChars.test(formData.name))
      newErrors.name = "Name contains invalid characters";

    if (formData.symbol && !/^[A-Z0-9]{2,10}$/.test(formData.symbol))
      newErrors.symbol =
        "Symbol must be 2–10 uppercase letters or numbers (no spaces)";

    if (
      formData.category &&
      !categories.some((c) => c._id === formData.category)
    )
      newErrors.category = "Invalid category selected";

    if (
      formData.description &&
      (formData.description.length < 10 ||
        formData.description.length > 200 ||
        !validChars.test(formData.description))
    )
      newErrors.description =
        "Description must be 10–200 valid characters (letters, numbers, -, _)";

    // ✅ Royalty % validation
    if (formData.royaltyBps !== "" && formData.royaltyBps !== null) {
      const royaltyValue = Number(formData.royaltyBps);
      if (isNaN(royaltyValue) || royaltyValue < 0 || royaltyValue > 100) {
        newErrors.royaltyBps = "Royalty % must be between 0 and 100";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ✅ Handle image upload
  // const handleImageChange = (e) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;

  //   const validTypes = ["image/png", "image/jpeg", "image/jpg"];
  //   const newErrors = { ...errors };

  //   if (!validTypes.includes(file.type)) {
  //     newErrors.image = "Only PNG, JPG, or JPEG allowed";
  //     return setErrors(newErrors);
  //   }
  //   if (file.size > 5 * 1024 * 1024) {
  //     newErrors.image = "Image size must not exceed 5MB";
  //     return setErrors(newErrors);
  //   }

  //   delete newErrors.image;
  //   setErrors(newErrors);
  //   setFormData((prev) => ({ ...prev, image: file }));
  //   setImagePreview(URL.createObjectURL(file));
  // };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        image: "Only PNG, JPG, or JPEG allowed",
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must not exceed 5MB",
      }));
      return;
    }

    setLoading(true);
    try {
      const ipfsUrl = await uploadFileToIPFS(file);
      console.log("ipfsUrl----- : ", ipfsUrl);

      setFormData((prev) => ({ ...prev, image: ipfsUrl }));
      setImagePreview(ipfsUrl);
      setErrors((prev) => ({ ...prev, image: "" }));
      toast.success("Image uploaded to IPFS successfully");
    } catch (error) {
      console.error(error);
      setErrors((prev) => ({ ...prev, image: error.message }));
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Submit form (create/update collection)
  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      for (const key in formData) {
        if (formData[key]) formDataToSend.append(key, formData[key]);
      }

      if (isUpdate) {
        formDataToSend.append("collectionAddress", data?.address);
      }

      let result;

      if (isCreate) {
        if (!isConnected) return toast.error("Wallet not connected");
        if (!walletClient || !publicClient)
          return toast.error("Wallet or Public Client not available");

        // ✅ Deploy contract (adjust based on wagmi/viem version)
        const txHash = await walletClient.deployContract({
          abi: contractData.abi,
          bytecode: contractData.bytecode,
          args: [
            formData.name,
            formData.symbol,
            formData.contractURI || "",
            formData.saleRecipient ||
              "0x0000000000000000000000000000000000000000",
            formData.royaltyRecipient ||
              "0x0000000000000000000000000000000000000000",
            Number(formData.royaltyBps || 0),
            formData.trustedForwarder ||
              "0x0000000000000000000000000000000000000000",
          ],
        });

        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash,
        });

        if (receipt?.contractAddress) {
          formDataToSend.append("txHash", receipt.transactionHash);
          formDataToSend.append("address", receipt.contractAddress);
        }

        result = await axios.post(
          `${BASE_URL}/collection/create`,
          formDataToSend,
          {
            headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        result = await axios.put(
          `${BASE_URL}/collection/update`,
          formDataToSend,
          {
            headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
          }
        );
      }

      if (result?.data?.success) {
        toast.success(
          `Collection ${isCreate ? "created" : "updated"} successfully`
        );
        handleClose(true);
      } else {
        toast.error(result?.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("error...", error);
      toast.error("Error while saving collection");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Render input fields
  const renderField = (label, name, placeholder, type = "text") => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type={type}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        readOnly={isView}
        className={errors[name] ? "is-invalid" : ""}
        placeholder={placeholder}
      />
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </Form.Group>
  );

  const renderTextAreaField = (label, name) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        readOnly={isView}
        className={errors[name] ? "is-invalid" : ""}
        placeholder={`Enter ${label.toLowerCase()}`}
      />
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </Form.Group>
  );

  const renderSelectField = (label, name, options) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Select
        name={name}
        value={formData[name]}
        onChange={handleChange}
        disabled={isView}
        className={errors[name] ? "is-invalid" : ""}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((option) => (
          <option key={option._id} value={option._id}>
            {option.name}
          </option>
        ))}
      </Form.Select>
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </Form.Group>
  );

  const renderImageField = () => (
    <Form.Group className="mb-3">
      <Form.Label>Image</Form.Label>
      <Form.Control
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className={errors.image ? "is-invalid" : ""}
      />
      {imagePreview && (
        <div className="mt-3 text-center">
          <img
            src={imagePreview}
            alt="Preview"
            className="img-fluid rounded"
            style={{ maxHeight: "200px", objectFit: "cover" }}
          />
        </div>
      )}
      {errors.image && <div className="invalid-feedback">{errors.image}</div>}
    </Form.Group>
  );

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={show}
        onHide={() => {
          handleClose();
          setFormData(defaultCollection);
          setErrors({});
          setImagePreview(null);
        }}
        centered
        className="modal-bg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isCreate && "Create Collection"}
            {isUpdate && "Update Collection"}
            {isView && "View Collection"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                {renderField("Name", "name", "Name")}
              </div>
              <div className="col-md-6">
                {renderField("Symbol", "symbol", "Symbol")}
              </div>
              <div className="col-md-12">
                {renderField(
                  "Default Sale Recipient",
                  "saleRecipient",
                  "Sale Recipient"
                )}
              </div>
              <div className="col-md-8">
                {renderField(
                  "Royalty Recipient",
                  "royaltyRecipient",
                  "Royalty Recipient"
                )}
              </div>
              <div className="col-md-4">
                {renderField(
                  "Royalty in %",
                  "royaltyBps",
                  "Royalty %",
                  "number"
                )}
              </div>

              <div className="col-md-12">
                {renderSelectField("Category", "category", categories)}
              </div>
              <div className="col-md-12">
                {renderTextAreaField("Description", "description")}
              </div>
            </div>
            {renderImageField()}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div style={{ color: "red", fontSize: "12px" }}>
            <div>* Only JPEG, JPG, and PNG files allowed</div>
            <div>* Max File Size: 5 MB</div>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              handleClose();
              setFormData(defaultCollection);
              setErrors({});
              setImagePreview(null);
            }}
          >
            Close
          </Button>
          {!isView && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              // disabled={loading || Object.keys(errors).length > 0}>
              disabled={loading}
            >
              {loading ? "Saving..." : isCreate ? "Create" : "Update"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddNftCollection;
